import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  Clock,
  MessageCircle,
  CheckCircle,
  AlertCircle,
  Loader,
  Leaf,
  Headphones
} from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '',
    subject: '',
    message: '' 
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // 1. Mapear el valor del select a un texto legible
    const subjectMap = {
      catalogo: "Consulta sobre Catálogo Digital",
      calculadora: "Soporte Calculadora PV/SV",
      dni: "Dudas Verificador de DNI",
      eventos: "Gestión de Eventos",
      afiliacion: "Quiero afiliarme a Mi DXN",
      otros: "Otros"
    };
    const subjectText = subjectMap[formData.subject] || formData.subject;

    // 2. Construir el mensaje con formato de WhatsApp (usando * para negrita y \n para saltos de línea)
    const whatsappMessage = `¡Hola! Quiero contactar con Mi DXN 🌿

*Nombre:* ${formData.name}
*Email:* ${formData.email}
*Teléfono:* ${formData.phone || 'No proporcionado'}
*Asunto:* ${subjectText}

*Mensaje:*
${formData.message}`;

    // 3. Codificar el mensaje para la URL
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/51968039152?text=${encodedMessage}`;

    // 4. Simular un pequeño tiempo de carga para buena UX y luego abrir WhatsApp
    setTimeout(() => {
      window.open(whatsappUrl, '_blank');
      setIsSubmitting(false);
      setSubmitStatus('success');
      
      // Limpiar el formulario
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });

      // Ocultar el mensaje de éxito después de 5 segundos
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 1500);
  };

  const contactInfo = [
    { 
      icon: Mail, 
      label: 'Email de Soporte', 
      value: 'soporte@midxn.com',
      link: 'mailto:soporte@midxn.com',
      description: 'Respuesta garantizada en 24 horas'
    },
    { 
      icon: Phone, 
      label: 'WhatsApp / Teléfono', 
      value: '+51 968039152',
      link: 'https://wa.me/51968039152',
      description: 'Lun - Sáb: 9:00 AM - 8:00 PM'
    },
    { 
      icon: MapPin, 
      label: 'Oficinas', 
      value: 'Av. Principal 123, Oficina 405',
      link: '#',
      description: 'Lima, Perú'
    },
    { 
      icon: Headphones, 
      label: 'Soporte Prioritario', 
      value: 'Atención a Distribuidores',
      link: '#',
      description: 'Canal exclusivo para usuarios Mi DXN'
    }
  ];

  const services = [
    'Catálogo Digital',
    'Calculadora PV/SV',
    'Verificador DNI',
    'Gestión de Eventos',
    'Soporte Técnico',
    'Capacitación'
  ];

  return (
    <section id="contacto" className="relative py-24 lg:py-32 bg-gradient-to-br from-stone-50 via-emerald-50/30 to-amber-50/30 overflow-hidden">
      {/* Elementos decorativos de fondo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-amber-200/30 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Encabezado */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl mb-6 shadow-lg shadow-emerald-500/20"
          >
            <MessageCircle className="w-10 h-10 text-white" />
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
            Hablemos de tu Crecimiento en <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">DXN</span>
          </h2>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
            ¿Tienes dudas sobre Mi DXN o necesitas ayuda para potenciar tu red? 
            Nuestro equipo de soporte está listo para ayudarte a alcanzar tus metas.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Información de contacto */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
            className="lg:col-span-1 space-y-6"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-stone-200">
              <h3 className="text-2xl font-bold text-stone-900 mb-6 flex items-center">
                <span className="w-2 h-8 bg-gradient-to-b from-emerald-500 to-teal-600 rounded-full mr-3"></span>
                Información de Contacto
              </h3>
              
              <div className="space-y-4">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <motion.a
                      key={info.label}
                      href={info.link}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-start p-4 rounded-2xl hover:bg-emerald-50/50 transition-all duration-300 group cursor-pointer"
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-1 group-hover:from-emerald-200 group-hover:to-teal-200 transition-colors">
                        <Icon className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div className="ml-4">
                        <p className="font-semibold text-stone-900 group-hover:text-emerald-600 transition-colors">
                          {info.label}
                        </p>
                        <p className="text-stone-800 font-medium">{info.value}</p>
                        <p className="text-sm text-stone-500 mt-1">{info.description}</p>
                      </div>
                    </motion.a>
                  );
                })}
              </div>
            </div>

            {/* Servicios rápidos */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-3xl p-6 text-white shadow-lg"
            >
              <div className="flex items-center gap-2 mb-4">
                <Leaf className="w-5 h-5 text-emerald-200" />
                <h4 className="font-bold text-lg">Funcionalidades Mi DXN</h4>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {services.map((service, index) => (
                  <motion.span
                    key={service}
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-sm bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 text-center border border-white/10"
                  >
                    {service}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Formulario */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true, margin: "-100px" }}
            className="lg:col-span-2"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-stone-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-stone-900 flex items-center">
                  <span className="w-2 h-8 bg-gradient-to-b from-amber-500 to-orange-500 rounded-full mr-3"></span>
                  Envíanos un Mensaje
                </h3>
                <div className="flex items-center space-x-2 text-sm text-stone-500">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                  <span>Soporte en línea</span>
                </div>
              </div>

              <AnimatePresence>
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-start gap-3"
                  >
                    <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-emerald-800">¡Redirigiendo a WhatsApp!</p>
                      <p className="text-emerald-700 text-sm">Tu mensaje ha sido preparado. Por favor, envíalo desde la ventana que se abrió.</p>
                    </div>
                  </motion.div>
                )}

                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-2xl flex items-start gap-3"
                  >
                    <AlertCircle className="w-6 h-6 text-rose-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-rose-800">Error al enviar</p>
                      <p className="text-rose-700 text-sm">Por favor, verifica tus datos e intenta nuevamente.</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Tu nombre completo"
                      className="w-full px-4 py-3 border border-stone-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all bg-white/50 placeholder:text-stone-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@email.com"
                      className="w-full px-4 py-3 border border-stone-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all bg-white/50 placeholder:text-stone-400"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Teléfono / WhatsApp
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+51 9XX XXX XXX"
                      className="w-full px-4 py-3 border border-stone-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all bg-white/50 placeholder:text-stone-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Asunto *
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all bg-white/50 text-stone-700"
                      required
                    >
                      <option value="">Selecciona un asunto</option>
                      <option value="catalogo">Consulta sobre Catálogo Digital</option>
                      <option value="calculadora">Soporte Calculadora PV/SV</option>
                      <option value="dni">Dudas Verificador de DNI</option>
                      <option value="eventos">Gestión de Eventos</option>
                      <option value="afiliacion">Quiero afiliarme a Mi DXN</option>
                      <option value="otros">Otros</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Describe tu consulta o el problema que estás experimentando..."
                    rows="5"
                    className="w-full px-4 py-3 border border-stone-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all resize-none bg-white/50 placeholder:text-stone-400"
                    required
                  ></textarea>
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white py-4 rounded-2xl font-semibold flex items-center justify-center space-x-3 shadow-lg shadow-emerald-600/20 hover:shadow-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>Preparando mensaje...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Enviar por WhatsApp</span>
                    </>
                  )}
                </motion.button>

                <p className="text-center text-sm text-stone-500">
                  Al enviar este formulario, serás redirigido a WhatsApp con tus datos pre-cargados.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;